import { OptionalExceptFor } from "@iventis/types/useful.types";
import { addSecondsToTimeSpan, coordinatesToString, ZERO_TIME_SPAN } from "@iventis/utilities";
import GeoJSON, { LineString, Feature } from "geojson";
import { MapObjectProperties, RouteWaypoint } from "@iventis/map-types";
import booleanPointOnLine from "@turf/boolean-point-on-line";
import { point } from "@turf/helpers";
import { CompositionMapObject, RouteApiFunctions, RouteFinderResponse } from "../types/internal";
import { ObjectUpdate } from "../types/events-store";
import { splitLine } from "./geojson-helpers";

export const createBasicRouteWaypoint = (position: GeoJSON.Position): RouteWaypoint => ({ coordinates: position, name: coordinatesToString(position) });

const createBasicRouteFinderResponse = (waypoints: OptionalExceptFor<RouteWaypoint, "coordinates">[]): RouteFinderResponse => ({
    route: { type: "LineString", coordinates: waypoints.map((waypoint) => waypoint.coordinates) },
    waypoints: waypoints.map((waypoint) => createBasicRouteWaypoint(waypoint.coordinates)),
    duration: 0,
});

export const generateRouteFeatureAndWaypoints = async (
    routeFinder: () => Promise<RouteFinderResponse>,
    options: {
        preGeneratedFeature: GeoJSON.Feature<GeoJSON.LineString, MapObjectProperties>;
        preGeneratedWaypoints: OptionalExceptFor<RouteWaypoint, "coordinates">[];
        durationProperty?: string;
    }
) => {
    const { route: geometry, waypoints, duration }: RouteFinderResponse =
        options.preGeneratedWaypoints.length < 2 ? createBasicRouteFinderResponse(options.preGeneratedWaypoints) : await routeFinder();

    // Apply the duration and the geometry to the latest transformation

    const feature: GeoJSON.Feature<GeoJSON.LineString, MapObjectProperties> = {
        ...options.preGeneratedFeature,
        properties: {
            ...options.preGeneratedFeature.properties,
            ...(options.durationProperty && { [options.durationProperty]: addSecondsToTimeSpan(ZERO_TIME_SPAN, duration) }),
        },
        geometry,
    };

    return { feature, waypoints } as const;
};

/** Splits a route map object into two new route map objects. Will handle splitting waypoints to correct route */
export async function splitRoute(
    splitFeatures: Feature<LineString, MapObjectProperties>[],
    originalRoute: CompositionMapObject,
    routeDurationProperty: string,
    splitPoint: { lng: number; lat: number },
    routeApiFunctions: RouteApiFunctions
) {
    if (splitFeatures.length === 1) {
        return [
            {
                geometry: splitFeatures[0].geometry,
                properties: splitFeatures[0].properties,
                objectId: splitFeatures[0].properties.id,
                layerId: splitFeatures[0].properties.layerid,
                waypoints: originalRoute.waypoints,
            },
        ];
    }

    if (splitFeatures.length > 2) {
        throw new Error("Splitting a route into more than 2 routes is not supported");
    }

    // Work what waypoints belong to what routes
    const waypointSplitIndex = findWaypointIndexOfLineSplit(originalRoute.waypoints, originalRoute.geojson as Feature<LineString>, splitPoint);

    if (waypointSplitIndex === -1) {
        throw new Error("Could not find the split point between two waypoints");
    }

    const newRoutes: ObjectUpdate[] = [];
    // For each of the two split features
    for (const [index, line] of splitFeatures.entries()) {
        // Get new way points
        const waypoints = createWaypointsForSplitFeature(waypointSplitIndex, index, originalRoute.waypoints, splitFeatures);
        // Populate route data
        const route = await generateRouteFeatureAndWaypoints(() => routeApiFunctions.routeFinder(waypoints, line.properties.modeOfTransport), {
            preGeneratedFeature: line,
            preGeneratedWaypoints: waypoints,
            durationProperty: routeDurationProperty,
        });

        newRoutes.push({
            geometry: route.feature.geometry,
            properties: route.feature.properties,
            objectId: route.feature.properties.id,
            layerId: route.feature.properties.layerid,
            waypoints: route.waypoints,
        });
    }
    return newRoutes;
}

/** Finds the index of the two waypoints that the split point is between */
function findWaypointIndexOfLineSplit(waypoints: RouteWaypoint[], route: Feature<LineString>, splitPoint: { lng: number; lat: number }) {
    let foundIndex = -1;
    // For each way point, check if the splitPoint was within them
    for (const [index, waypoint] of waypoints.entries()) {
        const nextWaypoint = waypoints[index + 1];

        if (nextWaypoint == null) {
            continue;
        }

        // Split the original route by the first waypoint
        const [waypointLng, waypointLat] = waypoint.coordinates;
        const firstSplit = splitLine(route, { lng: waypointLng, lat: waypointLat });
        // For the first waypoint we only get one feature back from splitting, as we are splitting at the start of the line
        const firstSplitFeature = index === 0 ? firstSplit.features[0] : firstSplit.features[1];

        // Take the split line from above and split again with second waypoint
        const [nextWaypointLng, nextWaypointLat] = nextWaypoint.coordinates;
        const secondSplit = splitLine(firstSplitFeature as Feature<LineString>, { lng: nextWaypointLng, lat: nextWaypointLat });
        const waypointLineSegment = secondSplit.features[0];

        // Check if the split point is on the line segement we have produced from two waypoints
        const isSplitPointOnWaypointLineSegment = booleanPointOnLine(point([splitPoint.lng, splitPoint.lat]), waypointLineSegment, { epsilon: 5e-8 });

        if (isSplitPointOnWaypointLineSegment) {
            foundIndex = index;
            break;
        }
    }
    return foundIndex;
}

/** Creates new array of waypoints for split feature */
function createWaypointsForSplitFeature(waypointSplitIndex: number, route: number, originalWaypoints: RouteWaypoint[], splitFeatures: Feature<LineString>[]) {
    if (route === 0) {
        const firstFeatureCoords = splitFeatures[0].geometry.coordinates;
        return [...originalWaypoints.slice(0, waypointSplitIndex + 1), { coordinates: firstFeatureCoords[firstFeatureCoords.length - 1] }];
    }

    if (route === 1) {
        const secondFeatureCoords = splitFeatures[1].geometry.coordinates;
        return [{ coordinates: secondFeatureCoords[0] }, ...originalWaypoints.slice(waypointSplitIndex + 1)];
    }

    throw new Error(`Route parameter has to be either '0' or '1'`);
}
